import React, {forwardRef, MutableRefObject, useEffect, useLayoutEffect, useRef} from 'react';
import Quill from 'quill';
import QuillImageDropAndPaste, { ImageData } from 'quill-image-drop-and-paste'
import QuillResize from 'quill-resize-module';
import 'quill/dist/quill.snow.css';
import {fileImageFilter, uploadFile} from "./upload";
import {messageDialog} from "./dialogs";
import Toolbar from "quill/modules/toolbar";

Quill.register('modules/imageDropAndPaste', QuillImageDropAndPaste)
Quill.register('modules/resize', QuillResize)

const uploadUrlMap: Record<string, string> = {}

async function imageHandler(this: QuillImageDropAndPaste, imageDataUrl: string | ArrayBuffer, type?: string, imageData?: ImageData) {
    if(!imageData) {
        return
    }
    const blob = imageData.toBlob()

    const uploadResults = await uploadFile(blob, {
        typeFilter: fileImageFilter
    })
    if ('error' in uploadResults) {
        messageDialog({
            label: "Error uploading image",
            text: uploadResults.error,
        })
    } else {
        uploadUrlMap[uploadResults.previewUrl] = uploadResults.id

        let index = (this.quill.getSelection() || {}).index
        if (index === undefined || index < 0) index = this.quill.getLength()
        this.quill.insertEmbed(index, 'image', uploadResults.previewUrl, 'user')
    }
}

// TODO: replace all other direct uses of Quill with this editor instead
// Adapted from https://github.com/slab/quill/blob/539cbffd0a13b18e9c65eb84dd35e6596e403158/packages/quill/src/core/quill.ts#L702
export default forwardRef<Quill, {
    value: string,
    readOnly?: boolean,
    withImages?: boolean,
    onChange: (value: string) => void,
}>(function Editor({ readOnly, withImages, value, onChange }, externalRef) {
    const ref = useRef<Quill>();
    const containerRef = useRef<HTMLDivElement>(null);
    const valueRef = useRef(value);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        ref.current?.enable(!readOnly);
    }, [ref, readOnly]);

    useEffect(() => {
        const container = containerRef.current;
        if(!container) {
            return;
        }
        const editorContainer = container.appendChild(
            container.ownerDocument.createElement('div'),
        );
        const quill = new Quill(editorContainer, {
            theme: 'snow',
            modules: {
                toolbar: [
                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                    [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                    [{'color': []}, {'background': []}, {'size': []}],
                    ['link', (withImages ? 'image' : null)].filter(x => x != null),
                    ['clean']
                ],
                imageDropAndPaste: withImages ? {
                    handler: imageHandler
                } : null,
                resize: {}
            }
        });
        (quill.getModule('toolbar') as Toolbar).addHandler('image', function (clicked) {
            if (clicked) {
                let fileInput = this.container?.querySelector('input.ql-image[type=file]')
                if (fileInput == null) {
                    fileInput = document.createElement('input')
                    const realFileInput = fileInput as HTMLInputElement
                    fileInput.setAttribute('type', 'file')
                    fileInput.setAttribute(
                        'accept',
                        'image/png, image/gif, image/jpeg, image/bmp, image/x-icon'
                    )
                    fileInput.classList.add('ql-image')
                    fileInput.addEventListener('change', function (e) {
                        const files = (e.target as HTMLInputElement).files!
                        let file: File
                        if (files.length > 0) {
                            file = files[0]
                            const type = file.type
                            const reader = new FileReader()
                            reader.onload = async (e) => {
                                // handle the inserted image
                                const dataUrl = e.target!.result!
                                const boundHandler = imageHandler.bind(quill.getModule('imageDropAndPaste') as QuillImageDropAndPaste)
                                await boundHandler(dataUrl, type, new ImageData(dataUrl, type, file.name))
                                realFileInput.value = ''
                            }
                            reader.readAsDataURL(file)
                        }
                    })
                }
                (fileInput as HTMLInputElement).click()
            }
        })

        ref.current = quill;
        if(externalRef) {
            if (typeof externalRef == 'function') {
                externalRef(quill);
            } else {
                externalRef.current = quill
            }
        }

        if (valueRef.current) {
            quill.setContents(quill.clipboard.convert({html: valueRef.current}));
        }

        quill.on(Quill.events.TEXT_CHANGE, () => {
            // See https://github.com/slab/quill/issues/4509#issuecomment-2521334016
            let contents = quill.getSemanticHTML().replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ');
            for(const [url, id] of Object.entries(uploadUrlMap)) {
                contents = contents.replaceAll(url, `/uploads/${id}`);
            }
            valueRef.current = contents
            onChangeRef.current(contents);
        });

        return () => {
            ref.current = undefined;
            if(externalRef) {
                if (typeof externalRef == 'function') {
                    externalRef(null);
                } else {
                    externalRef.current = null
                }
            }
            container.innerHTML = '';
        };
    }, [containerRef, ref]);

    useEffect(() => {
        if(valueRef.current != value) {
            console.log("Updating editor contents to", value);
            ref.current?.setContents(ref.current.clipboard.convert({html: value}));
        }
    }, [value]);

    return <div ref={containerRef}></div>;
})
